import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Breadcrumbs,
  Link,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useUser } from '@/providers';
import { Link as RouterLink, useParams } from 'react-router';

import ErrorSnackBar from '../../shared/ErrorSnackBar';
import ExecutionLogs from '../../shared/ExecutionLogs';
import { fetchJob } from '../../../actions/jobs';
import { clearErrorMessage } from '../../../actions/errorMessage';
import JOB_TYPES from '../types';
import LoadingMask from '../../shared/LoadingMask';

export default function JobExecutionLogs() {
  const dispatch = useDispatch();
  const { type: typeParam, job_uuid } = useParams();

  const api_error = useSelector((state) => state.errorMessage);
  const jobs = useSelector((state) => state.jobs || []);
  const user = useUser();

  // Get job from store reactively
  const job = jobs.find((job) => job.job_uuid === job_uuid);
  const [jobLoading, setJobLoading] = useState(true);
  const [hasTriedFetch, setHasTriedFetch] = useState(false);

  // Get job type object from route param
  const jobType = Object.entries(JOB_TYPES)
    .map(([key, value]) => {
      return value.value === typeParam ? value : undefined;
    })
    .filter((type) => type)[0];

  useEffect(() => {
    dispatch(clearErrorMessage());
  }, [dispatch]);

  useEffect(() => {
    if (!user) {
      return;
    }

    // If job is already available, stop loading
    if (job) {
      setJobLoading(false);
      return;
    }

    // If we haven't tried fetching yet, fetch the job
    if (!hasTriedFetch) {
      setHasTriedFetch(true);
      (async () => {
        try {
          if (user?.selected_project) {
            await dispatch(fetchJob(user.selected_project, job_uuid));
          }
        } catch (error) {
          // Error will be handled by ErrorSnackBar
        } finally {
          setJobLoading(false);
        }
      })();
    } else {
      // We've already tried fetching and job is still not available
      setJobLoading(false);
    }
  }, [dispatch, job, job_uuid, user, hasTriedFetch]);

  if (!user || jobLoading) {
    return <LoadingMask />;
  }

  if (!job || !jobType) {
    return (
      <Container>
        <Typography>Job not found</Typography>
      </Container>
    );
  }

  // Determine if worker filter should be enabled (TRAINING jobs)
  const enableWorkerFilter = job.type === JOB_TYPES.TRAINING.value;

  // Extract workers array from job
  const workers = job.workers || [];

  // Normalize job entity to have 'id' property for shared component
  // (jobs use 'job_uuid' but shared component expects 'id')
  const normalizedEntity = {
    ...job,
    id: job.job_uuid,
  };

  return (
    <Container>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 12, lg: 12 }}>
              <Breadcrumbs aria-label='breadcrumb'>
                <Link
                  underline='hover'
                  color='inherit'
                  component={RouterLink}
                  to={`/dashboard`}
                >
                  Home
                </Link>
                <Link
                  underline='hover'
                  color='inherit'
                  component={RouterLink}
                  to={`/jobs/${typeParam}`}
                >
                  {jobType.label}
                </Link>
                <Typography color='text.primary'>Execution Logs</Typography>
              </Breadcrumbs>
            </Grid>
            <br />
            <ExecutionLogs
              entity={normalizedEntity}
              entity_type='job'
              workers={workers}
              enableWorkerFilter={enableWorkerFilter}
            />
          </Grid>
          <ErrorSnackBar message={api_error} />
        </Container>
  );
}
